import Config from '../../config';
import { FeedActions, getFeedActions } from './FeedState';
import {
  isCashieringUpdate,
  isExpiredToken,
  isOpenPriceUpdate,
  isOrderUpdate,
  isQuote,
  isSettlement,
  isSpread,
  WebsocketFeedParser,
} from '@tradingblock/api';
import { FeedQuote, FeedSettlement } from '@tradingblock/types';
import { OrderFeedActions } from '../global/actions/OrderFeedActions';
import _ from 'lodash';
import { SessionActions } from '../global/actions';
import { AdminCashieringActions } from '../global/actions/admin/AdminCashieringActions';
import { QuoteDataActions } from '../global/actions/QuoteActions';
import { batch } from 'react-redux';
// Quotes batching worker (initialized when PERF batching is enabled)
let quotesWorker: Worker | null = null;
let hasSetupQuotesWorkerListener = false;
// Spreads batching worker (initialized when PERF batching is enabled)
let spreadsWorker: Worker | null = null;
let hasSetupSpreadsWorkerListener = false;

// Last QuoteTime/SpreadTime (as ms) sent to Redux per symbol - only dispatch if newer
const lastQuoteTimeSentToRedux = new Map<string, number>();
const lastSpreadTimeSentToRedux = new Map<string, number>();

function toTimestamp(val: string | number | null | undefined): number {
  if (val == null) return 0;
  if (typeof val === 'number') return val;
  const parsed = new Date(val).getTime();
  return isNaN(parsed) ? 0 : parsed;
}

// WebSocketService.ts
class WebSocketService {
  private static instance: WebSocketService;
  private ws: WebSocket | null = null;

  private constructor() {}

  // Singleton instance access
  public static getInstance(): WebSocketService {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
      console.log('websocket instance created');
    }
    return WebSocketService.instance;
  }

  // Initialize WebSocket connection
  public async connect(
    dispatch: React.Dispatch<FeedActions>,
    dispatcher: any,
    globalDispatch: any,
    fullDispatcher?: any
  ) {
    try {
      if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
        this.ws = new WebSocket(Config.tradingApiWebsockets);

        this.ws.onopen = () => {
          dispatcher.connected();
          getFeedActions(dispatch).setStatus('open');
          if (Config.perf && Config.perf.batchedQuotes && typeof Worker !== 'undefined') {
            const publicUrl = process.env.PUBLIC_URL || '';
            if (!quotesWorker) {
              try {
                quotesWorker = new Worker(`${publicUrl}/quotesWorker.js`);
                quotesWorker.postMessage({ type: 'CONFIG', intervalMs: Config.perf.quotesBatchIntervalMs });
              } catch (error) {
                console.error('Failed to create quotesWorker:', error);
              }
            }
            if (quotesWorker && !hasSetupQuotesWorkerListener && fullDispatcher) {
              quotesWorker.onmessage = (ev: MessageEvent) => {
                const data = ev.data as any;
                if (data && data.type === 'QUOTE_DELTAS' && data.quotes) {
                  const quotes = data.quotes;
                  const filtered: Record<string, any> = {};
                  for (const symbol of Object.keys(quotes)) {
                    const quote = quotes[symbol];
                    const quoteTs = toTimestamp(quote.QuoteTime);
                    const lastTs = lastQuoteTimeSentToRedux.get(symbol);
                    const lastTsVal = lastTs != null ? lastTs : 0;
                    if (quoteTs > lastTsVal) {
                      filtered[symbol] = quote;
                      lastQuoteTimeSentToRedux.set(symbol, quoteTs);
                    }
                  }
                  if (Object.keys(filtered).length > 0) {
                    batch(() => fullDispatcher.feed.updateQuotes(filtered));
                  }
                }
              };
              hasSetupQuotesWorkerListener = true;
            }
            if (!spreadsWorker) {
              try {
                spreadsWorker = new Worker(`${publicUrl}/spreadsWorker.js`);
                spreadsWorker.postMessage({ type: 'CONFIG', intervalMs: Config.perf.quotesBatchIntervalMs });
              } catch (error) {
                console.error('Failed to create spreadsWorker:', error);
              }
            }
            if (spreadsWorker && !hasSetupSpreadsWorkerListener && fullDispatcher) {
              spreadsWorker.onmessage = (ev: MessageEvent) => {
                const data = ev.data as any;
                if (data && data.type === 'SPREAD_DELTAS' && data.spreads) {
                  const spreads = data.spreads;
                  const filtered: Record<string, any> = {};
                  for (const symbol of Object.keys(spreads)) {
                    const spread = spreads[symbol];
                    const spreadTs = toTimestamp(spread.SpreadTime);
                    const lastTs = lastSpreadTimeSentToRedux.get(symbol);
                    const lastTsVal = lastTs != null ? lastTs : 0;
                    if (spreadTs > lastTsVal) {
                      filtered[symbol] = spread;
                      lastSpreadTimeSentToRedux.set(symbol, spreadTs);
                    }
                  }
                  if (Object.keys(filtered).length > 0) {
                    batch(() => fullDispatcher.feed.updateSpreads(filtered));
                  }
                }
              };
              hasSetupSpreadsWorkerListener = true;
            }
          }
        };

        this.ws.onmessage = (ev: MessageEvent) => {
          const val = ev.data;
          const parser = new WebsocketFeedParser(val);

          const msg = parser.parse();
          if (_.isNil(msg)) {
            console.warn('unknown feed type', val);
          } else if (!msg.isValid) {
            console.warn('errored feed message', val);
          } else if (isQuote(msg)) {
            if (Config.perf && Config.perf.batchedQuotes && quotesWorker) {
              quotesWorker.postMessage({ type: 'QUOTE', quote: msg });
            } else {
              getFeedActions(dispatch).onQuote(msg, val);
            }
          } else if (isSpread(msg)) {
            if (Config.perf && Config.perf.batchedQuotes && spreadsWorker) {
              spreadsWorker.postMessage({ type: 'SPREAD', spread: msg });
            } else {
              // Add to FeedState queue for FeedSync processing
              getFeedActions(dispatch).onSpread(msg, val);
            }
          } else if (isSettlement(msg)) {
            const settlement = msg as FeedSettlement;
            getFeedActions(dispatch).onSettlement(settlement, val);
            // When batched quotes is enabled, FeedSync is disabled - settlement must be
            // dispatched directly to quote reducer or it never reaches global state
            if (Config.perf && Config.perf.batchedQuotes && fullDispatcher) {
              batch(() =>
                fullDispatcher.feed.updateQuotes({
                  [settlement.Symbol]: {
                    ...settlement,
                    LastTradePrice: settlement.ClosePrice,
                  } as unknown as FeedQuote,
                })
              );
            }
          } else if (isOrderUpdate(msg)) {
            getFeedActions(dispatch).onOrderUpdate(msg, val);
            globalDispatch(OrderFeedActions.receivedUpdate({ order: msg }));
          } else if (isCashieringUpdate(msg)) {
            globalDispatch(
              AdminCashieringActions.setLatestCashieringUpdateDataFromWebsocket({
                latestCashieringUpdateDataFromWebsocket: msg,
              })
            );
          } else if (isOpenPriceUpdate(msg)) {
            globalDispatch(QuoteDataActions.receiveOpenPrice(msg));
          } else if (isExpiredToken(msg)) {
            console.warn('expired token message', val);
            globalDispatch(SessionActions.expired({ code: msg.Code, reason: msg.Reason }));
          }
        };

        this.ws.onclose = (ev: CloseEvent) => {
          if (ev.reason) {
            getFeedActions(dispatch).setCloseReason(ev.reason);
            console.warn(ev.reason);
          }
          getFeedActions(dispatch).setStatus('closed');
        };

        this.ws.onerror = error => {
          dispatcher.errored({ error: error, reason: 'Unable to connect', message: 'Datafeed error' });
          getFeedActions(dispatch).setStatus('error');
          console.error('WebSocket error:', error);
        };
      }
    } catch (createWebsocketError) {
      dispatcher.errored({
        error: createWebsocketError,
        reason: 'Error creating websocket',
        message: 'Websocket error',
      });
    }
  }

  // Send message to WebSocket
  public sendMessage(data: string | ArrayBufferLike | Blob | ArrayBufferView) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data);
    } else if (this.ws) {
      console.warn('trying to send message to closed/closing websocket', data);
    }
  }

  public shouldTryReconnect = () => {
    if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
      return true;
    }
    return false;
  };

  // Check if websocket is currently open
  public isConnected = () => {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  };

  // Get current connection state
  public getConnectionState = () => {
    if (!this.ws) return 'null';
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'open';
      case WebSocket.CLOSING:
        return 'closing';
      case WebSocket.CLOSED:
        return 'closed';
      default:
        return 'unknown';
    }
  };

  // Close WebSocket connection
  public closeConnection() {
    if (this.ws) {
      this.ws.close();
    }
  }
}

export const webSocketService = WebSocketService.getInstance();
